/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import * as components from "../components/index.js";
import { TeralError } from "./teralerror.js";

/**
 * Error response when a required service is temporarily unavailable. This indicates that the service exists but cannot be reached or is not responding.
 *
 * @remarks
 *
 * When you encounter this error:
 * - The service is likely experiencing temporary issues
 * - Retrying the request after a short delay may succeed
 * - If the error persists, the service may be undergoing maintenance
 * - Contact Teral support if the issue continues
 */
export type ServiceUnavailableErrorResponseData = {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;
};

/**
 * Error response when a required service is temporarily unavailable. This indicates that the service exists but cannot be reached or is not responding.
 *
 * @remarks
 *
 * When you encounter this error:
 * - The service is likely experiencing temporary issues
 * - Retrying the request after a short delay may succeed
 * - If the error persists, the service may be undergoing maintenance
 * - Contact Teral support if the issue continues
 */
export class ServiceUnavailableErrorResponse extends TeralError {
  /**
   * Metadata object included in every API response. This provides context about the request and is essential for debugging, audit trails, and support inquiries. The `requestId` is particularly important when troubleshooting issues with the Teral support team.
   */
  meta: components.Meta;
  /**
   * Base error structure following Problem Details for HTTP APIs (RFC 7807). This provides a standardized way to carry machine-readable details of errors in HTTP response content.
   */
  error: components.BaseError;

  /** The original data that was passed to this error instance. */
  data$: ServiceUnavailableErrorResponseData;

  constructor(
    err: ServiceUnavailableErrorResponseData,
    httpMeta: { response: Response; request: Request; body: string },
  ) {
    const message = "message" in err && typeof err.message === "string"
      ? err.message
      : `API error occurred: ${JSON.stringify(err)}`;
    super(message, httpMeta);
    this.data$ = err;
    this.meta = err.meta;
    this.error = err.error;

    this.name = "ServiceUnavailableErrorResponse";
  }
}

/** @internal */
export const ServiceUnavailableErrorResponse$inboundSchema: z.ZodType<
  ServiceUnavailableErrorResponse,
  z.ZodTypeDef,
  unknown
> = z.object({
  meta: components.Meta$inboundSchema,
  error: components.BaseError$inboundSchema,
  request$: z.instanceof(Request),
  response$: z.instanceof(Response),
  body$: z.string(),
})
  .transform((v) => {
    return new ServiceUnavailableErrorResponse(v, {
      request: v.request$,
      response: v.response$,
      body: v.body$,
    });
  });
